import { useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

type ResourceType = 'tasks' | 'members' | 'households';

export interface PlanLimitResult {
  allowed: boolean;
  current: number;
  limit: number;
  tier: string;
  upgradeUrl?: string;
}

/**
 * Hook for checking free-tier resource limits before writes.
 *
 * Usage:
 *   const { checkLimit, isChecking } = usePlanLimits(householdId);
 *   const result = await checkLimit('tasks');
 *   if (!result.allowed) { // show UpgradeModal }
 */
export function usePlanLimits(householdId: string | null): {
  checkLimit: (resourceType: ResourceType) => Promise<PlanLimitResult>;
  isChecking: boolean;
} {
  const [isChecking, setIsChecking] = useState(false);

  const checkLimit = useCallback(
    async (resourceType: ResourceType): Promise<PlanLimitResult> => {
      if (!householdId) {
        return { allowed: true, current: 0, limit: Infinity, tier: 'FREE' };
      }

      setIsChecking(true);
      try {
        const functions = getFunctions(undefined, 'us-central1');
        const enforce = httpsCallable<
          { householdId: string; resourceType: ResourceType; action: string },
          PlanLimitResult
        >(functions, 'enforceFreeTierLimitsOnWrite');

        const response = await enforce({
          householdId,
          resourceType,
          action: 'create',
        });

        return response.data;
      } finally {
        setIsChecking(false);
      }
    },
    [householdId]
  );

  return { checkLimit, isChecking };
}
